import {
  shellApprovalReviewSchema,
  shellCommandResultSchema,
  shellCommandSchema,
  shellProjectCreateInputSchema,
  shellSnapshotSchema,
  type ShellApprovalGate,
  type ShellCommand,
  type ShellCommandPort,
  type ShellCommandResult,
  type ShellProjectCreateInput,
  type ShellSnapshot,
  type ShellStageRecord,
  type ShellSurface,
} from "@ppt-agent/contracts";
import { z } from "zod";

const approvalLabels: Record<ShellApprovalGate, string> = {
  brief: "任务书",
  storyline: "故事线",
  visual_direction: "视觉方向",
};

const gateOrder = [
  "brief",
  "storyline",
  "visual_direction",
] as const satisfies readonly ShellApprovalGate[];

const gateMarkers: Record<ShellApprovalGate, string> = {
  brief: "a",
  storyline: "b",
  visual_direction: "c",
};

interface LocalProjectState {
  input: ShellProjectCreateInput;
  gateIndex: number;
  revisions: Record<ShellApprovalGate, number>;
  generationPolls: number;
}

const portableStateSchema = z
  .object({
    schemaVersion: z.literal("1.0.0"),
    format: z.literal("ppt-agent-shell-continuation"),
    projectId: z.string().trim().min(1),
    input: shellProjectCreateInputSchema,
    gateIndex: z.literal(3),
    revisions: z
      .object({
        brief: z.number().int().positive(),
        storyline: z.number().int().positive(),
        visual_direction: z.number().int().positive(),
      })
      .strict(),
    generationPolls: z.number().int().min(3),
  })
  .strict();

export class ShellCommandRejectedError extends Error {
  constructor(summary: string) {
    super(summary);
    this.name = "ShellCommandRejectedError";
  }
}

function artifactHash(gate: ShellApprovalGate, revision: number): `sha256:${string}` {
  const markerCode = gateMarkers[gate].charCodeAt(0) + revision - 1;
  const marker = String.fromCharCode(Math.min(markerCode, 102));
  return `sha256:${marker.repeat(64)}`;
}

function pendingCopy(state: LocalProjectState, gate: ShellApprovalGate) {
  const revision = state.revisions[gate];
  const common = {
    gate,
    artifactHash: artifactHash(gate, revision),
    artifactRevision: revision,
  };
  if (gate === "brief") {
    return {
      ...common,
      title: "确认演示任务书",
      summary: state.input.objective,
      details: [
        `受众：${state.input.audience}`,
        `页数：${state.input.slideCount} 页`,
        `资料：${state.input.sourceNames.length || 0} 项`,
      ],
    };
  }
  if (gate === "storyline") {
    return {
      ...common,
      title: "确认故事线与页面顺序",
      summary: "从问题与证据开始，收束到明确的决策和下一步。",
      details: ["开场与结论", "关键证据", "方案比较", "行动路线"],
    };
  }
  return {
    ...common,
    title: "确认整套视觉方向",
    summary: "编辑型商业叙事，使用安静的中性色和克制的紫色强调。",
    details: ["Aptos 原生文字", "16:9 网格", "图表与表格保持可编辑"],
  };
}

function progressState(state: LocalProjectState): {
  runStatus: ShellSnapshot["runStatus"];
  completed: number;
  label: string;
  deliveryReady: boolean;
} {
  if (state.gateIndex < 3) {
    const statuses = ["AWAITING_BRIEF_APPROVAL", "AWAITING_OUTLINE_APPROVAL", "AWAITING_VISUAL_APPROVAL"] as const;
    return { runStatus: statuses[state.gateIndex] ?? "AWAITING_BRIEF_APPROVAL", completed: state.gateIndex,
      label: "已完成 " + state.gateIndex + " / 3 个确认", deliveryReady: false };
  }
  if (state.generationPolls === 0) {
    const completed = Math.min(2, state.input.slideCount);
    return { runStatus: "GENERATING", completed, label: "正在生成第 " + Math.min(completed + 1, state.input.slideCount) + " / " + state.input.slideCount + " 页", deliveryReady: false };
  }
  if (state.generationPolls === 1) {
    return { runStatus: "RENDERING", completed: state.input.slideCount, label: "正在渲染 " + state.input.slideCount + " 页预览", deliveryReady: false };
  }
  if (state.generationPolls === 2) {
    return { runStatus: "EVALUATING", completed: state.input.slideCount, label: "正在检查内容与版式", deliveryReady: false };
  }
  return { runStatus: "COMPLETED", completed: state.input.slideCount, label: state.input.slideCount + " 页已通过检查", deliveryReady: true };
}

function buildStageRecords(state: LocalProjectState, progress: ReturnType<typeof progressState>, slides: ShellSnapshot["slides"]): ShellStageRecord[] {
  const phase = state.gateIndex === 0 ? 1 : state.gateIndex < 3 ? 2 : state.generationPolls < 3 ? 3 : 4;
  const stageStatus = (index: number): ShellStageRecord["status"] => progress.deliveryReady || index < phase ? "completed" : index === phase ? "current" : "pending";
  const slideDetails = slides.length ? slides.map((slide) => "第 " + slide.order + " 页 · " + slide.title) : ["页面尚未生成"];
  return [
    { stage: "intent", label: "定意", status: stageStatus(1), summary: state.input.objective,
      details: ["受众：" + state.input.audience, "页数：" + state.input.slideCount + " 页", ...(state.input.sourceNames.length ? state.input.sourceNames : ["仅使用当前输入的目标与要求"])] },
    { stage: "direction", label: "谋篇", status: stageStatus(2), summary: state.gateIndex >= 1 ? "从主张、证据到行动形成统一叙事与视觉方向" : "叙事与视觉尚未开始",
      details: state.gateIndex >= 1 ? ["故事线与页面顺序", "整套视觉方向", "三个人审门保持独立"] : ["等待任务书确认"] },
    { stage: "creation", label: "成章", status: stageStatus(3), summary: state.gateIndex === 3 ? progress.label : "页面生成与质量检查尚未开始",
      details: progress.deliveryReady ? [...slideDetails, "必要文字、可编辑对象与 PPTX 结构通过检查"] : slideDetails },
    { stage: "delivery", label: "交付", status: stageStatus(4), summary: progress.deliveryReady ? "可编辑文件已准备" : "交付尚未开始",
      details: progress.deliveryReady ? ["PowerPoint 演示文稿", "可继续编辑的项目包"] : ["将在整套质量检查通过后准备"] },
  ];
}

function buildSnapshot(state: LocalProjectState): ShellSnapshot {
  const progress = progressState(state);
  const pendingGate = gateOrder[state.gateIndex];
  const slides = Array.from({ length: state.input.slideCount }, (_, index) => ({
    slideId: `slide-${index + 1}`,
    order: index + 1,
    title: [
      state.input.name,
      "今天需要做出的决定",
      "证据指向一个清晰窗口",
      "方案在收益与风险间取平衡",
      "执行路径已经具备",
      "下一步与负责人",
    ][index] ?? `支持页面 ${index + 1}`,
    purpose: index === 0 ? state.input.objective : `支持核心结论的第 ${index + 1} 个论据`,
    stateLabel:
      state.gateIndex < 3
        ? "等待生成"
        : progress.deliveryReady || index < progress.completed
          ? "已生成"
          : "生成中",
  }));
  return shellSnapshotSchema.parse({
    schemaVersion: "1.0.0",
    projectId: state.input.projectId,
    projectName: state.input.name,
    surface: state.input.surface,
    runStatus: progress.runStatus,
    sourceNames: state.input.sourceNames,
    stageRecords: buildStageRecords(state, progress, state.gateIndex < 3 ? [] : slides),
    approvals: gateOrder.map((gate, index) => ({
      gate,
      label: approvalLabels[gate],
      status:
        index < state.gateIndex
          ? "completed"
          : index === state.gateIndex
            ? "current"
            : "pending",
    })),
    ...(pendingGate ? { pendingApproval: pendingCopy(state, pendingGate) } : {}),
    progress: {
      completed: progress.completed,
      total: state.gateIndex < 3 ? 3 : state.input.slideCount,
      label: progress.label,
    },
    slides: state.gateIndex < 3 ? [] : slides,
    deliveryReady: progress.deliveryReady,
    portableContinuationReady: progress.deliveryReady,
    recentEvent: pendingGate
      ? `${approvalLabels[pendingGate]}草案已保存，批准前不会继续。`
      : progress.label,
  });
}

function encodeUtf8Base64(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return globalThis.btoa(binary);
}

function decodeUtf8Base64(value: string): string {
  const binary = globalThis.atob(value);
  return new TextDecoder().decode(
    Uint8Array.from(binary, (character) => character.charCodeAt(0)),
  );
}

async function hashText(value: string): Promise<`sha256:${string}`> {
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(value),
  );
  return `sha256:${[...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("")}`;
}

export class LocalVerticalSliceCommandPort implements ShellCommandPort {
  private readonly projects = new Map<string, LocalProjectState>();

  constructor(private readonly surface: ShellSurface) {}

  async execute(rawCommand: ShellCommand): Promise<ShellCommandResult> {
    const command = shellCommandSchema.parse(rawCommand);
    if (command.type === "create_project") {
      const input = shellProjectCreateInputSchema.parse(command.input);
      if (input.surface !== this.surface) {
        throw new ShellCommandRejectedError("Project surface does not match this application.");
      }
      const existing = this.projects.get(input.projectId);
      if (existing) {
        if (existing.input.idempotencyKey !== input.idempotencyKey) {
          throw new ShellCommandRejectedError("Project identifier already exists.");
        }
        return shellCommandResultSchema.parse({ kind: "snapshot", value: buildSnapshot(existing) });
      }
      const state: LocalProjectState = {
        input,
        gateIndex: 0,
        revisions: { brief: 1, storyline: 1, visual_direction: 1 },
        generationPolls: 0,
      };
      this.projects.set(input.projectId, state);
      return shellCommandResultSchema.parse({ kind: "snapshot", value: buildSnapshot(state) });
    }

    if (command.type === "open_portable") {
      let decoded: unknown;
      try {
        decoded = JSON.parse(decodeUtf8Base64(command.contentBase64));
      } catch {
        throw new ShellCommandRejectedError("Portable project is not valid UTF-8 JSON.");
      }
      const portable = portableStateSchema.parse(decoded);
      const state: LocalProjectState = {
        input: { ...portable.input, surface: "windows" },
        gateIndex: portable.gateIndex,
        revisions: portable.revisions,
        generationPolls: portable.generationPolls,
      };
      this.projects.set(portable.projectId, state);
      return shellCommandResultSchema.parse({ kind: "snapshot", value: buildSnapshot(state) });
    }

    if (command.type === "inspect_progress") return { kind: "progress", value: null };
    const projectId = command.type === "review_approval" ? command.input.projectId : command.projectId;
    const state = this.projects.get(projectId);
    if (!state) throw new ShellCommandRejectedError("Project was not found.");

    if (command.type === "read_snapshot" || command.type === "retry_planning") return shellCommandResultSchema.parse({ kind: "snapshot", value: buildSnapshot(state) });
    if (command.type === "inspect") {
      if (state.gateIndex === 3 && state.generationPolls < 3) state.generationPolls += 1;
      return shellCommandResultSchema.parse({ kind: "snapshot", value: buildSnapshot(state) });
    }

    if (command.type === "review_approval") {
      const review = shellApprovalReviewSchema.parse(command.input);
      const expectedGate = gateOrder[state.gateIndex];
      if (!expectedGate || review.gate !== expectedGate) {
        throw new ShellCommandRejectedError("Approval gates must be completed in order.");
      }
      if (
        review.artifactHash !== artifactHash(expectedGate, state.revisions[expectedGate]) ||
        review.artifactRevision !== state.revisions[expectedGate]
      ) {
        throw new ShellCommandRejectedError("Approval must match the exact pending artifact revision.");
      }
      if (review.status === "changes_requested") {
        state.revisions[expectedGate] += 1;
      } else {
        state.gateIndex += 1;
      }
      return shellCommandResultSchema.parse({ kind: "snapshot", value: buildSnapshot(state) });
    }

    if (!state || !progressState(state).deliveryReady) {
      throw new ShellCommandRejectedError("Delivery files are not ready.");
    }
    const isPortable = command.type === "export_portable";
    const content = isPortable
      ? JSON.stringify({
          schemaVersion: "1.0.0",
          format: "ppt-agent-shell-continuation",
          projectId: state.input.projectId,
          input: state.input,
          gateIndex: 3,
          revisions: state.revisions,
          generationPolls: 3,
        })
      : `Flowisle PPT editable delivery for ${state.input.projectId}`;
    return shellCommandResultSchema.parse({
      kind: "download",
      value: {
        fileName: isPortable ? `${state.input.projectId}.pptagent.json` : `${state.input.projectId}.pptx`,
        mediaType: isPortable
          ? "application/vnd.ppt-agent.project+json"
          : "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        contentBase64: encodeUtf8Base64(content),
        sha256: await hashText(content),
      },
    });
  }
}

export function createLocalVerticalSliceCommandPort(
  surface: ShellSurface,
): ShellCommandPort {
  return new LocalVerticalSliceCommandPort(surface);
}
